import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { 
  Box, 
  Toolbar, 
  useTheme, 
  useMediaQuery,
  CssBaseline,
  Container,
  Paper,
  Fade,
  Slide,
  Typography
} from '@mui/material';
import ModernNavbar from './ModernNavbar';
import ModernSidebar from './ModernSidebar';
import Breadcrumbs from './Breadcrumbs';
import '../styles/layout.css';

const drawerWidth = 280;

const Layout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const isDarkMode = theme.palette.mode === 'dark';

  // Don't show breadcrumbs on dashboard
  const showBreadcrumbs = location.pathname !== '/';

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Auto-close drawer on screen size change
  useEffect(() => {
    if (!isMobileOrTablet && drawerOpen) {
      setDrawerOpen(false);
    }
  }, [isMobileOrTablet, drawerOpen]);

  // Manage body scroll when drawer is open on mobile
  useEffect(() => {
    if (isMobileOrTablet && drawerOpen) {
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }

    return () => {
      document.body.classList.remove('drawer-open');
    };
  }, [isMobileOrTablet, drawerOpen]);

  const handleToggleTheme = () => {
    const newTheme = !isDarkMode;
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    // Force a page reload to apply the new theme
    window.location.reload();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Modern Navigation */}
      <ModernNavbar
        onDrawerToggle={handleDrawerToggle}
        isDarkMode={isDarkMode}
        onToggleTheme={handleToggleTheme}
        drawerOpen={drawerOpen}
      />

      {/* Modern Sidebar */}
      <ModernSidebar
        drawerWidth={drawerWidth}
        isMobile={isMobileOrTablet}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
      
      {/* Main Content Area */}
      <Box
        component="main"
        className="main-content"
        sx={{
          flexGrow: 1,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
          minHeight: '100vh',
          // Responsive width and margin adjustments
          ...(isMobileOrTablet ? {
            width: '100%',
            marginLeft: 0,
            paddingLeft: 0,
            overflowX: 'hidden'
          } : {
            width: '100%',
            marginLeft: `${drawerWidth}px`,
            paddingLeft: 0,
            overflowX: 'hidden',
            boxSizing: 'border-box'
          })
        }}
      >
        {/* Remove the Toolbar spacing */}
        {/* <Toolbar /> */}
        
        {/* Content Container */}
        <Container 
          maxWidth={false}
          className="content-container"
          sx={{ 
            py: { xs: 1, sm: 2, md: 3 },
            px: { xs: 2, sm: 3, md: 4 },
            minHeight: 'calc(100vh - 64px)',
            width: '100%',
            maxWidth: 'none'
          }}
        >
          <Fade in timeout={300}>
            <Paper 
              elevation={0}
              sx={{
                p: { xs: 3 },
                padding: { sm: '37px' },
                paddingLeft: { sm: '8px' },
                paddingRight: { sm: '8px' },
                borderRadius: 4,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'divider',
                minHeight: 'calc(100vh - 160px)',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isDarkMode 
                  ? '0 4px 20px rgba(0,0,0,0.3)'
                  : '0 4px 20px rgba(0,0,0,0.08)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
                  borderRadius: '4px 4px 0 0'
                }
              }}
            >
              <Slide direction="up" in timeout={400}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {/* Breadcrumbs */}
                  {showBreadcrumbs && <Breadcrumbs />}
                  
                  {/* Page Content */}
                  <Box sx={{ mt: showBreadcrumbs ? 0 : 2 }}>
                    <Outlet />
                  </Box>
                </Box>
              </Slide>
            </Paper>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
